import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map, catchError, switchMap, tap, filter, concatMap, mergeMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { Router } from '@angular/router';
import { ShoppingListActions } from './shopping-list.actions';
import { MessageService } from 'primeng/api';
import { ShoppingListClient } from '@api/api-service';

@Injectable()
export class ShoppingListEffects {
    private actions$ = inject(Actions);
    private router = inject(Router);
    private messageService = inject(MessageService);
    private shoppingListClient = inject(ShoppingListClient);

    // #region Shopping List
    getAllShoppingLists$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ShoppingListActions.getAllShoppingLists),
            switchMap(() =>
                this.shoppingListClient.getShoppingLists().pipe(
                    map(shoppingLists => ShoppingListActions.getAllShoppingListsSuccess({ shoppingLists })),
                    catchError(error => of(ShoppingListActions.getAllShoppingListsFailure({ error: error?.message ?? 'Unknown error' }))),
                ),
            ),
        ),
    );

    getShoppingList$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ShoppingListActions.getShoppingList),
            switchMap(({ shoppingListId }) =>
                this.shoppingListClient.getShoppingListById(shoppingListId).pipe(
                    map(shoppingList => ShoppingListActions.getShoppingListSuccess({ shoppingList })),
                    catchError(error => of(ShoppingListActions.getShoppingListFailure({ error: error?.message ?? 'Unknown error' }))),
                ),
            ),
        ),
    );

    createShoppingList$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ShoppingListActions.createShoppingList),
            switchMap(({ name }) =>
                this.shoppingListClient.createShoppingList({ name }).pipe(
                    map(shoppingList => ShoppingListActions.createShoppingListSuccess({ shoppingList })),
                    catchError(error => of(ShoppingListActions.createShoppingListFailure({ error: error?.message ?? 'Unknown error' }))),
                ),
            ),
        ),
    );

    updateShoppingListName$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ShoppingListActions.updateShoppingListName),
            switchMap(({ shoppingListId, name }) =>
                this.shoppingListClient.updateShoppingList(shoppingListId, { name }).pipe(
                    map(shoppingList => ShoppingListActions.updateShoppingListNameSuccess({ shoppingList })),
                    catchError(error =>
                        of(ShoppingListActions.updateShoppingListNameFailure({ error: error?.message ?? 'Unknown error' })),
                    ),
                ),
            ),
        ),
    );

    deleteShoppingList$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ShoppingListActions.deleteShoppingList),
            switchMap(({ shoppingListId }) =>
                this.shoppingListClient.removeShoppingList(shoppingListId).pipe(
                    map(() => ShoppingListActions.deleteShoppingListSuccess({ shoppingListId })),
                    catchError(error => of(ShoppingListActions.deleteShoppingListFailure({ error: error?.message ?? 'Unknown error' }))),
                ),
            ),
        ),
    );

    redirectAfterDelete$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ShoppingListActions.deleteShoppingListSuccess),
                filter(({ shoppingListId }) => this.router.url.split('?')[0].endsWith(`/shopping-lists/${shoppingListId}`)),
                tap(() => this.router.navigate(['/shopping-lists'])),
            ),
        { dispatch: false },
    );
    // #endregion

    // #region Groups
    addGroupToShoppingList$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ShoppingListActions.addGroupToShoppingList),
            switchMap(({ shoppingListId, groupName }) =>
                this.shoppingListClient.addGroupToShoppingList(shoppingListId, { name: groupName }).pipe(
                    map(shoppingListGroup => ShoppingListActions.addGroupToShoppingListSuccess({ shoppingListId, shoppingListGroup })),
                    catchError(error =>
                        of(ShoppingListActions.addGroupToShoppingListFailure({ error: error?.message ?? 'Unknown error' })),
                    ),
                ),
            ),
        ),
    );

    updateShoppingListGroup$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ShoppingListActions.updateShoppingListGroupName),
            switchMap(({ shoppingListGroupId, name }) =>
                this.shoppingListClient.updateShoppingListGroup(shoppingListGroupId, { name }).pipe(
                    map(shoppingListGroup => ShoppingListActions.updateShoppingListGroupNameSuccess({ shoppingListGroup })),
                    catchError(error =>
                        of(ShoppingListActions.updateShoppingListGroupNameFailure({ error: error?.message ?? 'Unknown error' })),
                    ),
                ),
            ),
        ),
    );

    removeGroup$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ShoppingListActions.removeGroupFromShoppingList),
            switchMap(({ shoppingListGroupId }) =>
                this.shoppingListClient.removeGroupFromShoppingList(shoppingListGroupId).pipe(
                    map(() => ShoppingListActions.removeGroupFromShoppingListSuccess({ shoppingListGroupId })),
                    catchError(error =>
                        of(ShoppingListActions.removeGroupFromShoppingListFailure({ error: error?.message ?? 'Unknown error' })),
                    ),
                ),
            ),
        ),
    );
    // #endregion

    // #region Products
    addProductToShoppingList$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ShoppingListActions.addProductToShoppingList),
            switchMap(({ shoppingListId, productId, quantity, shoppingListGroupId }) =>
                this.shoppingListClient.addProductToShoppingList(shoppingListId, { productId, quantity, shoppingListGroupId }).pipe(
                    map(shoppingList => ShoppingListActions.addProductToShoppingListSuccess({ shoppingList })),
                    catchError(error =>
                        of(ShoppingListActions.addProductToShoppingListFailure({ error: error?.message ?? 'Unknown error' })),
                    ),
                ),
            ),
        ),
    );

    createShoppingListAndAddProduct$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ShoppingListActions.createShoppingListAndAddProduct),
            switchMap(({ name, productId, quantity }) =>
                this.shoppingListClient.createShoppingList({ name }).pipe(
                    switchMap(createdList => this.shoppingListClient.addProductToShoppingList(createdList.id, { productId, quantity })),
                    map(shoppingList => ShoppingListActions.createShoppingListAndAddProductSuccess({ shoppingList })),
                    catchError(error =>
                        of(ShoppingListActions.createShoppingListAndAddProductFailure({ error: error?.message ?? 'Unknown error' })),
                    ),
                ),
            ),
        ),
    );

    updateProductQuantity$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ShoppingListActions.updateProductQuantityInShoppingList),
            switchMap(({ shoppingListProductId, quantity, showMessage }) =>
                this.shoppingListClient.updateShoppingListProduct(shoppingListProductId, { quantity }).pipe(
                    map(shoppingListProduct =>
                        ShoppingListActions.updateProductQuantityInShoppingListSuccess({ shoppingListProduct, showMessage }),
                    ),
                    catchError(error =>
                        of(ShoppingListActions.updateProductQuantityInShoppingListFailure({ error: error?.message ?? 'Unknown error' })),
                    ),
                ),
            ),
        ),
    );

    removeProduct$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ShoppingListActions.removeProductFromShoppingList),
            mergeMap(({ shoppingListProductId }) =>
                this.shoppingListClient.removeProductFromShoppingList(shoppingListProductId).pipe(
                    map(() => ShoppingListActions.removeProductFromShoppingListSuccess({ shoppingListProductId })),
                    catchError(error =>
                        of(ShoppingListActions.removeProductFromShoppingListFailure({ error: error?.message ?? 'Unknown error' })),
                    ),
                ),
            ),
        ),
    );
    // #endregion

    // #region Comments

    addComment$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ShoppingListActions.addCommentToShoppingList),
            concatMap(({ shoppingListId, shoppingListProductId, text }) =>
                this.shoppingListClient.addCommentToShoppingList(shoppingListId, { shoppingListProductId, text }).pipe(
                    map(shoppingListComment => ShoppingListActions.addCommentToShoppingListSuccess({ shoppingListComment })),
                    catchError(error =>
                        of(ShoppingListActions.addCommentToShoppingListFailure({ error: error?.message ?? 'Unknown error' })),
                    ),
                ),
            ),
        ),
    );

    updateComment$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ShoppingListActions.updateCommentInShoppingList),
            mergeMap(({ shoppingListCommentId, text }) =>
                this.shoppingListClient.updateShoppingListComment(shoppingListCommentId, { text }).pipe(
                    map(shoppingListComment => ShoppingListActions.updateCommentInShoppingListSuccess({ shoppingListComment })),
                    catchError(error =>
                        of(ShoppingListActions.updateCommentInShoppingListFailure({ error: error?.message ?? 'Unknown error' })),
                    ),
                ),
            ),
        ),
    );

    removeComment$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ShoppingListActions.removeCommentFromShoppingList),
            mergeMap(({ shoppingListCommentId }) =>
                this.shoppingListClient.removeShoppingListComment(shoppingListCommentId).pipe(
                    map(() => ShoppingListActions.removeCommentFromShoppingListSuccess({ shoppingListCommentId })),
                    catchError(error =>
                        of(ShoppingListActions.removeCommentFromShoppingListFailure({ error: error?.message ?? 'Unknown error' })),
                    ),
                ),
            ),
        ),
    );
    // #endregion

    // #region Notifications
    // #region ShoppingList
    MESSAGE_SERVICE_CreateShoppingListSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ShoppingListActions.createShoppingListSuccess),
                tap(() => {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Skapad',
                        detail: 'Listan har skapats.',
                    });
                }),
            ),
        { dispatch: false },
    );

    MESSAGE_SERVICE_CreateShoppingListFailure$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ShoppingListActions.createShoppingListFailure),
                tap(({ error }) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Misslyckades',
                        detail: error || 'Kunde inte skapa listan.',
                    });
                }),
            ),
        { dispatch: false },
    );

    MESSAGE_SERVICE_UpdateShoppingListNameSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ShoppingListActions.updateShoppingListNameSuccess),
                tap(() => {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Uppdaterad',
                        detail: 'Listans namn har uppdaterats.',
                    });
                }),
            ),
        { dispatch: false },
    );

    MESSAGE_SERVICE_UpdateShoppingListNameFailure$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ShoppingListActions.updateShoppingListNameFailure),
                tap(({ error }) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Misslyckades',
                        detail: error || 'Kunde inte uppdatera listans namn.',
                    });
                }),
            ),
        { dispatch: false },
    );

    MESSAGE_SERVICE_DeleteShoppingListSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ShoppingListActions.deleteShoppingListSuccess),
                tap(() => {
                    this.messageService.add({
                        severity: 'info',
                        summary: 'Borttagen',
                        detail: 'Listan har tagits bort.',
                    });
                }),
            ),
        { dispatch: false },
    );

    MESSAGE_SERVICE_DeleteShoppingListFailure$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ShoppingListActions.deleteShoppingListFailure),
                tap(({ error }) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Misslyckades',
                        detail: error || 'Kunde inte ta bort listan.',
                    });
                }),
            ),
        { dispatch: false },
    );
    // #endregion

    // #region Products
    MESSAGE_SERVICE_AddProductToShoppingListSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ShoppingListActions.addProductToShoppingListSuccess),
                tap(() => {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Tillagd',
                        detail: 'Produkten har lagts till i listan.',
                    });
                }),
            ),
        { dispatch: false },
    );

    MESSAGE_SERVICE_AddProductToShoppingListFailure$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ShoppingListActions.addProductToShoppingListFailure),
                tap(({ error }) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Misslyckades',
                        detail: error || 'Kunde inte lägga till produkten i listan.',
                    });
                }),
            ),
        { dispatch: false },
    );

    MESSAGE_SERVICE_CreateShoppingListAndAddProductSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ShoppingListActions.createShoppingListAndAddProductSuccess),
                tap(() => {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Tillagd',
                        detail: 'Listan skapades och produkten lades till.',
                    });
                }),
            ),
        { dispatch: false },
    );

    MESSAGE_SERVICE_CreateShoppingListAndAddProductFailure$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ShoppingListActions.createShoppingListAndAddProductFailure),
                tap(({ error }) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Misslyckades',
                        detail: error || 'Kunde inte skapa listan och lägga till produkten.',
                    });
                }),
            ),
        { dispatch: false },
    );

    MESSAGE_SERVICE_UpdateProductQuantitySuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ShoppingListActions.updateProductQuantityInShoppingListSuccess),
                filter(({ showMessage }) => !!showMessage),
                tap(() => {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Tillagd',
                        detail: 'Antalet i listan har uppdaterats.',
                    });
                }),
            ),
        { dispatch: false },
    );

    MESSAGE_SERVICE_UpdateProductQuantityFailure$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ShoppingListActions.updateProductQuantityInShoppingListFailure),
                tap(({ error }) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Misslyckades',
                        detail: error || 'Kunde inte uppdatera antalet.',
                    });
                }),
            ),
        { dispatch: false },
    );

    MESSAGE_SERVICE_RemoveProductSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ShoppingListActions.removeProductFromShoppingListSuccess),
                tap(() => {
                    this.messageService.add({
                        severity: 'info',
                        summary: 'Borttagen',
                        detail: 'Produkten har tagits bort från listan.',
                    });
                }),
            ),
        { dispatch: false },
    );

    MESSAGE_SERVICE_RemoveProductFailure$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ShoppingListActions.removeProductFromShoppingListFailure),
                tap(({ error }) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Misslyckades',
                        detail: error || 'Kunde inte ta bort produkten från listan.',
                    });
                }),
            ),
        { dispatch: false },
    );
    // #endregion

    // #region Groups
    MESSAGE_SERVICE_RemoveGroupSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ShoppingListActions.removeGroupFromShoppingListSuccess),
                tap(() => {
                    this.messageService.add({
                        severity: 'info',
                        summary: 'Borttagen',
                        detail: 'Gruppen har tagits bort från listan.',
                    });
                }),
            ),
        { dispatch: false },
    );

    MESSAGE_SERVICE_RemoveGroupFailure$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ShoppingListActions.removeGroupFromShoppingListFailure),
                tap(({ error }) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Misslyckades',
                        detail: error || 'Kunde inte ta bort gruppen från listan.',
                    });
                }),
            ),
        { dispatch: false },
    );

    MESSAGE_SERVICE_AddGroupToShoppingListSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ShoppingListActions.addGroupToShoppingListSuccess),
                tap(() => {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Skapad',
                        detail: 'Gruppen har skapats.',
                    });
                }),
            ),
        { dispatch: false },
    );

    MESSAGE_SERVICE_AddGroupToShoppingListFailure$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ShoppingListActions.addGroupToShoppingListFailure),
                tap(({ error }) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Misslyckades',
                        detail: error || 'Kunde inte skapa gruppen.',
                    });
                }),
            ),
        { dispatch: false },
    );

    MESSAGE_SERVICE_UpdateShoppingListGroupNameSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ShoppingListActions.updateShoppingListGroupNameSuccess),
                tap(() => {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Uppdaterad',
                        detail: 'Gruppens namn har uppdaterats.',
                    });
                }),
            ),
        { dispatch: false },
    );

    MESSAGE_SERVICE_UpdateShoppingListGroupNameFailure$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ShoppingListActions.updateShoppingListGroupNameFailure),
                tap(({ error }) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Misslyckades',
                        detail: error || 'Kunde inte uppdatera gruppens namn.',
                    });
                }),
            ),
        { dispatch: false },
    );
    // #endregion

    // #region Comments
    MESSAGE_SERVICE_AddCommentFailure$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ShoppingListActions.addCommentToShoppingListFailure),
                tap(({ error }) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Misslyckades',
                        detail: error || 'Kunde inte lägga till kommentaren.',
                    });
                }),
            ),
        { dispatch: false },
    );

    MESSAGE_SERVICE_UpdateCommentFailure$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ShoppingListActions.updateCommentInShoppingListFailure),
                tap(({ error }) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Misslyckades',
                        detail: error || 'Kunde inte uppdatera kommentaren.',
                    });
                }),
            ),
        { dispatch: false },
    );

    MESSAGE_SERVICE_RemoveCommentSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ShoppingListActions.removeCommentFromShoppingListSuccess),
                tap(() => {
                    this.messageService.add({
                        severity: 'info',
                        summary: 'Borttagen',
                        detail: 'Kommentaren har tagits bort.',
                    });
                }),
            ),
        { dispatch: false },
    );

    MESSAGE_SERVICE_RemoveCommentFailure$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ShoppingListActions.removeCommentFromShoppingListFailure),
                tap(({ error }) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Misslyckades',
                        detail: error || 'Kunde inte ta bort kommentaren.',
                    });
                }),
            ),
        { dispatch: false },
    );
    // #endregion
    // #endregion
}
